import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import SuppliersTable from './SuppliersTable';
import AddSupplierModal from './AddSupplierModal';
import EditSupplierModal from './EditSupplierModal';
import ViewSupplierModal from './ViewSupplierModal';
import DeleteSupplierModal from './DeleteSupplierModal';
import SupplierBillingStatementTable from './SupplierBillingStatementTable';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '../../services/SupplierService';
import PageHeader from '../../components/PageHeader/PageHeader';
import CustomTabPanel from '../../components/Tab/CustomTabPanel';
import { useTranslation } from 'react-i18next';
import BulkImportButton from '../../components/BulkImport/BulkImportButton';

const Suppliers = () => {
  const { t } = useTranslation();

  const [tabValue, setTabValue] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const searchTimeoutRef = useRef(null);

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const fetchSuppliers = useCallback(async (page, pageSize, search, currentSortModel = sortModel, currentFilterModel = filterModel) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const response = await getSuppliers(page, pageSize, cleanSearch, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      });
      
      // El backend devuelve { providers: [...], total: number, page: number, pageSize: number }
      if (response && typeof response === 'object') {
        setSuppliers(response.providers || response.data || []);
        setTotalRows(response.total || response.totalCount || 0);
      } else {
        // Fallback para compatibilidad con respuesta anterior
        setSuppliers(response || []);
        setTotalRows(response?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [sortModel, filterModel]);

  // Cargar datos iniciales solo una vez
  useEffect(() => {
    fetchSuppliers(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchSuppliers, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  // Funciones para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);

    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    const currentPageSize = paginationModel.pageSize;
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchSuppliers(1, currentPageSize, newSearchTerm, sortModel, filterModel);
    }, 500);
  }, [paginationModel.pageSize, sortModel, filterModel, fetchSuppliers]);

  const handleAddSupplier = async (newSupplier) => {
    try {
      await createSupplier(newSupplier);
      fetchSuppliers(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error creating supplier:', error);
    }
  };

  const handleEditSupplier = async (updatedSupplier) => {
    try {
      await updateSupplier(selectedSupplier.id, updatedSupplier);
      fetchSuppliers(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error updating supplier:', error);
    }
  };

  const handleDeleteSupplier = async () => {
    try {
      await deleteSupplier(selectedSupplier.id);
      setIsDeleteModalOpen(false);
      fetchSuppliers(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error deleting supplier:', error);
    }
  };

  return (
    <div>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleChangeTab} aria-label="suppliers tabs">
          <Tab label={t('suppliers')} />
          <Tab label={t('account_status') || 'Estado de Cuenta'} />
        </Tabs>
      </Box>

      <CustomTabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <PageHeader title={t('suppliers')} subtitle={t('manage_suppliers')} />
          <BulkImportButton
            type="providers"
            label={t('suppliers')}
            onSuccess={() => fetchSuppliers(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)}
          />
        </Box>
        <SuppliersTable
          loading={loading}
          suppliers={suppliers}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          totalRows={totalRows}
          onRefresh={() => fetchSuppliers(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel)}
          onAdd={() => setIsAddModalOpen(true)}
          onEdit={(supplier) => {
            setSelectedSupplier(supplier);
            setIsEditModalOpen(true);
          }}
          onView={(supplier) => {
            setSelectedSupplier(supplier);
            setIsViewModalOpen(true);
          }}
          onDelete={(supplier) => {
            setSelectedSupplier(supplier);
            setIsDeleteModalOpen(true);
          }}
        />
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        <PageHeader title={t('account_status') || 'Estado de Cuenta'} subtitle={t('account_status_subtitle') || 'Gestiona el estado de cuenta de los proveedores'} />
        <SupplierBillingStatementTable />
      </CustomTabPanel>

      <AddSupplierModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddSupplier}
      />
      <EditSupplierModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSupplier}
        supplier={selectedSupplier}
      />
      <ViewSupplierModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        supplier={selectedSupplier}
      />
      <DeleteSupplierModal
        supplierName={selectedSupplier ? selectedSupplier.businessName : ''}
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteSupplier}
        />
    </div>
  );
};

export default Suppliers;
